import React, { memo } from 'react';
import { Gavel, Tag, Wallet, Zap } from 'lucide-react';
import { getListingTypeLabel, getListingTypeStyle } from '../../utils/listingTypes';
import { useLanguage } from '../../App';

/**
 * ListingCardBadges
 * Displays various status badges:
 * - Listing type (Sale, Rent, Trade, etc.)
 * - Boosted badge (for promoted listings)
 * - Featured/Promoted badge
 * - USDT Friendly badge
 * - Auction live/ended status
 * - Yard sale status
 * - Sold/Reserved ribbons
 */

// Boosted Badge - Professional minimal badge (used inline with type badge)
export const BoostedBadge = memo(({ listing }) => {
  if (!listing.is_boosted || listing.status === 'sold' || listing.status === 'reserved') {
    return null;
  }
  
  return (
    <span 
      className="inline-flex items-center gap-1 px-2 py-0.5 bg-slate-800 dark:bg-slate-700 text-white text-[10px] font-medium rounded-full"
      data-testid={`boosted-badge-${listing.id}`}
    >
      <Zap className="w-2.5 h-2.5 fill-amber-400 text-amber-400" />
      PROMOTED
    </span>
  );
});

BoostedBadge.displayName = 'BoostedBadge';

// Status ribbon overlay - Horizontal banner across center of image
// Semi-transparent with consistent text size for all statuses
const StatusRibbon = memo(({ status, label, bgColor }) => (
  <div className="absolute inset-0 pointer-events-none z-20 flex items-center justify-center">
    {/* Semi-transparent overlay */}
    <div className="absolute inset-0 bg-black/15" />
    {/* Horizontal ribbon in center */}
    <div className={`absolute left-0 right-0 ${bgColor} backdrop-blur-[2px] py-2 shadow-lg`}>
      <span className="block text-white text-center font-bold text-xs uppercase tracking-wider">
        {label}
      </span>
    </div>
  </div>
));

StatusRibbon.displayName = 'StatusRibbon';

// Sold ribbon overlay
export const SoldRibbon = memo(() => (
  <StatusRibbon 
    status="sold" 
    label="Sold" 
    bgColor="bg-red-600/85" 
  />
));

SoldRibbon.displayName = 'SoldRibbon';

// Reserved ribbon overlay
export const ReservedRibbon = memo(() => (
  <StatusRibbon 
    status="reserved" 
    label="Reserved" 
    bgColor="bg-orange-500/85" 
  />
));

ReservedRibbon.displayName = 'ReservedRibbon';

// Under Offer ribbon overlay
export const UnderOfferRibbon = memo(({ t }) => (
  <StatusRibbon 
    status="under_offer" 
    label={t?.('status_under_offer') || 'Under Offer'} 
    bgColor="bg-amber-500/85" 
  />
));

UnderOfferRibbon.displayName = 'UnderOfferRibbon';

// Price Reduced Badge - Shows in top-left corner
export const PriceReducedBadge = memo(({ t }) => (
  <div className="absolute top-3 left-3 z-10">
    <span className="inline-flex items-center gap-1 px-2 py-1 bg-green-500 text-white text-[10px] font-bold rounded-full shadow-lg">
      <Tag className="w-3 h-3" />
      {t?.('price_reduced') || 'Verlaagd'}
    </span>
  </div>
));

PriceReducedBadge.displayName = 'PriceReducedBadge';

// USDT Friendly Badge - Prominent teal badge for crypto payments
export const USDTBadge = memo(({ listingId }) => (
  <div 
    className="absolute bottom-3 right-3 z-10"
    data-testid={`usdt-badge-${listingId}`}
  >
    <span className="inline-flex items-center gap-1 px-2 py-1 bg-gradient-to-r from-teal-500 to-emerald-500 text-white text-[10px] font-bold rounded-full shadow-lg ring-2 ring-white/30">
      <Wallet className="w-3 h-3" />
      USDT
    </span>
  </div>
));

USDTBadge.displayName = 'USDTBadge';

// Top-left badges - Currently empty since listing type is in its own badge
// and Featured is now shown as an icon in the top-right area
export const TopLeftBadges = memo(({ listing }) => {
  // No longer showing badges here - Featured moved to top-right
  return null;
});

TopLeftBadges.displayName = 'TopLeftBadges';

// Featured star icon - shown in top-right area next to favorite button
export const FeaturedIcon = memo(({ listing }) => {
  if (!listing.is_promoted || listing.status === 'sold' || listing.status === 'reserved') {
    return null;
  }
  
  return (
    <div 
      className="w-7 h-7 rounded-full flex items-center justify-center bg-amber-500 text-white shadow-md"
      title="Featured Listing"
      data-testid={`badge-featured-${listing.id}`}
    >
      <svg 
        xmlns="http://www.w3.org/2000/svg" 
        viewBox="0 0 24 24" 
        fill="currentColor" 
        className="w-4 h-4"
      >
        <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/>
      </svg>
    </div>
  );
});

FeaturedIcon.displayName = 'FeaturedIcon';

// Auction status badge - Position: top-left for consistency
export const AuctionStatusBadge = memo(({ listing }) => {
  if (listing.listing_type !== 'auction') return null;
  
  // Support both 'auction_end_time' (legacy) and 'end_time' (new format)
  const auctionEndTime = listing.auction?.auction_end_time || listing.auction?.end_time;
  const isLive = listing.status !== 'sold' && 
    auctionEndTime && 
    new Date(auctionEndTime) > new Date();
  
  return (
    <div className="absolute top-2 left-2 z-10">
      {isLive ? (
        <span className="inline-flex items-center gap-1 px-1.5 py-0.5 bg-green-500 text-white text-[10px] font-medium rounded-full shadow-md">
          <Gavel className="w-3 h-3" />
          Live
        </span>
      ) : (
        <span className="inline-flex items-center gap-1 px-1.5 py-0.5 bg-red-500 text-white text-[10px] font-medium rounded-full shadow-md">
          <Gavel className="w-3 h-3" />
          {listing.status === 'sold' ? 'Sold' : 'Ended'}
        </span>
      )}
    </div>
  );
});

AuctionStatusBadge.displayName = 'AuctionStatusBadge';

// Yard Sale status badge - Only shows Live/Ended, item count moved to header
export const YardSaleStatusBadge = memo(({ listing }) => {
  if (listing.listing_type !== 'yard_sale') return null;
  
  const isSold = listing.status === 'sold';
  const hasEndDate = listing.yard_sale?.end_date;
  const isLive = hasEndDate && new Date(listing.yard_sale.end_date) > new Date();
  const isEnded = hasEndDate && new Date(listing.yard_sale.end_date) <= new Date();
  
  // Only show badge for status changes, not item count
  if (!isSold && !isLive && !isEnded) return null;
  
  return (
    <div className="absolute top-2 left-2 z-10">
      {isSold ? (
        <span className="inline-flex items-center gap-1 px-1.5 py-0.5 bg-red-500 text-white text-[10px] font-medium rounded-full shadow-md">
          <Tag className="w-3 h-3" />
          Sold
        </span>
      ) : isLive ? (
        <span className="inline-flex items-center gap-1 px-1.5 py-0.5 bg-green-500 text-white text-[10px] font-medium rounded-full shadow-md">
          <Tag className="w-3 h-3" />
          Live
        </span>
      ) : isEnded ? (
        <span className="inline-flex items-center gap-1 px-1.5 py-0.5 bg-red-500 text-white text-[10px] font-medium rounded-full shadow-md">
          <Tag className="w-3 h-3" />
          Ended
        </span>
      ) : null}
    </div>
  );
});

YardSaleStatusBadge.displayName = 'YardSaleStatusBadge';

/**
 * ListingCardBadges - Combined component for all badges on image
 * Shows listing type + status in one combined badge
 * Priority system: Max 2-3 badges on photo
 * P1: Status (Sold/Reserved/Under Offer) - diagonal ribbon
 * P2: Type + Status combined badge + PROMOTED badge (top-left, stacked vertically)
 * P3: Info badge bottom-left (bid count, item count) - subtle glassmorphism
 * P4: Price Reduced badge (top-right, when applicable)
 */
const ListingCardBadges = memo(({ listing }) => {
  const { t, language } = useLanguage();
  
  // Auction bid count
  const bidCount = listing.listing_type === 'auction' ? (listing.auction?.bid_count || 0) : 0;
  
  // Yard sale item count
  const itemCount = listing.listing_type === 'yard_sale' ? (listing.yard_sale?.items?.length || 0) : 0;
  
  // Check if boosted (and not sold/reserved/under_offer)
  const showPromoted = listing.is_boosted && 
    listing.status !== 'sold' && 
    listing.status !== 'reserved' && 
    listing.status !== 'under_offer';
  
  return (
    <>
      {/* P1: Status ribbons - Always show (diagonal corner) */}
      {listing.status === 'sold' && <SoldRibbon />}
      {listing.status === 'reserved' && <ReservedRibbon />}
      {listing.status === 'under_offer' && <UnderOfferRibbon t={t} />}
      
      {/* P2: Badge stack - top left (Type badge above, Promoted badge below) */}
      <div className="absolute top-2 left-2 z-10 flex flex-col items-start gap-1">
        <CombinedTypeBadge listing={listing} language={language} inline />
        {showPromoted && <BoostedBadge listing={listing} />}
      </div>
      
      {/* P3: Info badge - bottom left, subtle glassmorphism */}
      {/* Auction bids count */}
      {listing.listing_type === 'auction' && bidCount > 0 && listing.status !== 'sold' && (
        <div className="absolute bottom-2 left-2 z-10">
          <span className="inline-flex items-center gap-1 px-1.5 py-0.5 bg-black/50 backdrop-blur-sm text-white text-[10px] font-medium rounded">
            {bidCount} {bidCount === 1 ? 'bid' : 'bids'}
          </span>
        </div>
      )}
      
      {/* Yard sale items count */}
      {listing.listing_type === 'yard_sale' && itemCount > 0 && listing.status !== 'sold' && (
        <div className="absolute bottom-2 left-2 z-10">
          <span className="inline-flex items-center gap-1 px-1.5 py-0.5 bg-black/50 backdrop-blur-sm text-white text-[10px] font-medium rounded">
            {itemCount} {itemCount === 1 ? 'item' : 'items'}
          </span>
        </div>
      )}
    </>
  );
});

/**
 * Combined Type + Status Badge
 * Shows listing type with status for auctions/yard sales
 * Uses consistent styling with ListingTypeBadge component
 * @param inline - When true, renders without absolute positioning (for badge stack)
 */
const CombinedTypeBadge = memo(({ listing, language = 'en', inline = false }) => {
  const listingType = listing.listing_type || 'sale';
  
  // Don't show badge if sold/reserved (ribbon already shows this)
  if (listing.status === 'sold' || listing.status === 'reserved') {
    return null;
  }
  
  // Get translated label and style from central config
  const style = getListingTypeStyle(listingType);
  const label = getListingTypeLabel(listingType, language);
  
  // Determine badge config based on type
  const getBadgeConfig = () => {
    switch (listingType) {
      case 'auction': {
        const auctionEndTime = listing.auction?.auction_end_time || listing.auction?.end_time;
        const isLive = auctionEndTime && new Date(auctionEndTime) > new Date();
        const liveText = language === 'nl' ? 'Live' : language === 'es' ? 'En vivo' : 'Live';
        const endedText = language === 'nl' ? 'Afgelopen' : language === 'es' ? 'Finalizado' : 'Ended';
        return {
          icon: Gavel,
          label: isLive ? `${label} • ${liveText}` : `${label} • ${endedText}`,
          bg: isLive ? 'bg-rose-600' : 'bg-gray-500',
          pulse: isLive
        };
      }
      case 'yard_sale': {
        const hasEndDate = listing.yard_sale?.end_date;
        const isLive = hasEndDate && new Date(listing.yard_sale.end_date) > new Date();
        const isEnded = hasEndDate && new Date(listing.yard_sale.end_date) <= new Date();
        const liveText = language === 'nl' ? 'Live' : language === 'es' ? 'En vivo' : 'Live';
        const endedText = language === 'nl' ? 'Afgelopen' : language === 'es' ? 'Finalizado' : 'Ended';
        return {
          icon: Tag,
          label: isLive ? `${label} • ${liveText}` : isEnded ? `${label} • ${endedText}` : label,
          bg: isLive ? 'bg-amber-500' : isEnded ? 'bg-gray-500' : 'bg-amber-500',
          pulse: isLive
        };
      }
      default:
        return {
          icon: null,
          label: label,
          bg: style.bg,
          pulse: false
        };
    }
  };
  
  const config = getBadgeConfig();
  const IconComponent = config.icon;
  
  const badge = (
    <span 
      className={`inline-flex items-center gap-1 px-2 py-0.5 ${config.bg} text-white text-[10px] font-medium rounded-full ${config.pulse ? 'animate-pulse' : ''}`}
      data-testid={`card-badge-${listingType}`}
    >
      {IconComponent && <IconComponent className="w-3 h-3" />}
      {config.label}
    </span>
  );
  
  // If inline mode (used in badge stack), return just the badge
  if (inline) {
    return badge;
  }
  
  // Otherwise wrap with absolute positioning (legacy support)
  return (
    <div className="absolute top-3 left-3 z-10">
      {badge}
    </div>
  );
});

CombinedTypeBadge.displayName = 'CombinedTypeBadge';

ListingCardBadges.displayName = 'ListingCardBadges';

export default ListingCardBadges;
